import { AsyncThunkAction } from '@reduxjs/toolkit';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo } from 'react';

import { AsyncState, RootState, useDispatch, useSelector } from 'src/store';
import { fetchAppointmentType, fetchClinic, fetchDoctor } from 'src/store/appointment';
import { fetchPatientHasDetails } from 'src/store/bookingFlow';

export function useAsyncSelector<TArgs, TData, TReturn = TData>(
    selector: (state: RootState) => AsyncState<TArgs, TReturn>,
    dispatchFunc: (args: TArgs) => AsyncThunkAction<TData, TArgs, object>,
    args?: TArgs
) {
    const dispatch = useDispatch();
    const asyncState = useSelector(selector);

    useEffect(() => {
        if (args && !isEqual(args, asyncState.request)) {
            dispatch(dispatchFunc(args));
        }
    }, [args, asyncState.request, dispatch, dispatchFunc]);

    return asyncState;
}

export function useClinicSelector() {
    const { clinicId } = useSelector((state) => state.appointment);

    return useAsyncSelector(
        useCallback((state) => state.appointment.clinic, []),
        fetchClinic,
        useMemo(() => ({ siteId: clinicId }), [clinicId])
    );
}

export function useDoctorSelector() {
    const { clinicId, doctorErpId } = useSelector((state) => state.appointment);

    return useAsyncSelector(
        useCallback((state) => state.appointment.doctor, []),
        fetchDoctor,
        useMemo(() => ({ siteId: clinicId, doctorId: doctorErpId }), [clinicId, doctorErpId])
    );
}

export function useAppointmentTypeSelector() {
    const { clinicId, appointmentTypeId } = useSelector((state) => state.appointment);

    return useAsyncSelector(
        useCallback((state) => state.appointment.appointmentType, []),
        fetchAppointmentType,
        useMemo(
            () =>
                (clinicId && appointmentTypeId && { siteId: clinicId, appointmentTypeId: appointmentTypeId }) ||
                undefined,
            [clinicId, appointmentTypeId]
        )
    );
}

export function usePatientHasDetails() {
    const clinicId = useSelector((state) => state.appointment.clinicId);

    return useAsyncSelector(
        useCallback((state) => state.bookingFlow.patientHasDetails, []),
        fetchPatientHasDetails,
        clinicId
    );
}
